'use client';

import Users from '@carvana/ds-icons/UsersCvna';
import LinkButton from '@carvana/ds-link-button';
import { icon } from '@carvana/ds-styles';
import Switch from '@carvana/ds-switch';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useOptimistic } from 'react';
import HydrationGatedButton from '@/common/HydrationGatedButton';
import { toggleCoSignerAction } from '~/app/(carvana-layout)/financing-rsc/actions';
import { ANALYTICS_EVENTS } from '~/constants/flp/analyticsEvents';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import { useModalContext } from '~/context/flp/ModalContext/ModalContext';
import { useCreditApplicationUpdate } from '~/context/flp-rsc/CreditApplicationUpdateContext';
import { usePricingUpdate } from '~/context/flp-rsc/PricingUpdateContext';
import useIsHydrated from '~/hooks/common/useIsHydrated';
import useToggleTracking from '~/hooks/flp-rsc/useToggleTracking';
import {
  Avatar,
  CoSignerContent,
  CoSignerDetails,
  Initials,
  Name
} from './CoSigner.styles';
import { CoSignerState } from '../../types';
import FinanceCard from '../FinanceCard';

const textOptions = {
  default: 'Adding a co-signer can reduce your minimum cash down.',
  enabled: 'Terms include your co-signer.',
  disabled: "Terms don't include your co-signer.",
  error:
    'We are having trouble retrieving your co-signer information. This will not be reflected in your pricing.'
};

type OwnProps = {
  coSigner: CoSignerState;
};

export default function CoSigner({ coSigner }: OwnProps) {
  const { data: coSignerData, error, enabled } = coSigner || {};
  const { setAddCoApplicantModalOpen, setEditCoApplicantModalOpen } =
    useModalContext();
  const { trackEvent } = useContext(AnalyticsContext);
  const isHydrated = useIsHydrated();
  const {
    isPending: isPricingPending,
    startTransition: startPricingTransition
  } = usePricingUpdate();
  const {
    isPending: isCreditApplicationPending,
    startTransition: startCreditApplicationTransition
  } = useCreditApplicationUpdate();
  const isPending = isPricingPending || isCreditApplicationPending;
  const trackToggle = useToggleTracking({
    analyticsClickEvent: ANALYTICS_EVENTS.PREQUALIFIED.TOGGLES_COSIGNER,
    datadogClickEvent: DATADOG_EVENTS.PREQUALIFIED.TOGGLES_COSIGNER,
    errorEvent: DATADOG_EVENTS.API.TOGGLE_COAPPLICANT_API_ERROR,
    location: 'Toggle Co-Signer',
    toggleType: 'cosigner'
  });

  const [optimisticEnabled, setOptimisticEnabled] = useOptimistic(
    Boolean(enabled)
  );

  const handleAddCosigner = () => {
    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.CLICKS_ADD_COSIGNER);
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.CLICKS_ADD_COSIGNER);
    setAddCoApplicantModalOpen(true);
  };

  const handleEditCoSigner = () => {
    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.CLICKS_EDIT_COSIGNER);
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.CLICKS_EDIT_COSIGNER);
    setEditCoApplicantModalOpen(true);
  };

  const handleToggleCosigner = () => {
    const nextState = !optimisticEnabled;
    let togglePromise: Promise<void>;

    startPricingTransition(() => {
      togglePromise = trackToggle(nextState, () => {
        setOptimisticEnabled(nextState);

        return toggleCoSignerAction();
      });

      return togglePromise;
    });
    startCreditApplicationTransition(() => togglePromise);
  };

  if (coSigner === null) {
    return null;
  }

  const hasError = Boolean(error);
  const hasCosigner = Boolean(coSignerData?.firstName);
  const coSignerInitials = hasCosigner
    ? `${coSignerData?.firstName[0]}${coSignerData?.lastName[0]}`
    : '';

  const CardToggle = () => {
    if (!hasCosigner || hasError) return null;

    return (
      <Switch
        aria-label="Include co-signer in terms"
        checked={optimisticEnabled}
        disabled={isPending}
        onChange={handleToggleCosigner}
      />
    );
  };

  const text = (() => {
    switch (true) {
      case hasError:
        return textOptions.error;
      case optimisticEnabled:
        return textOptions.enabled;
      case !optimisticEnabled && hasCosigner:
        return textOptions.disabled;
      default:
        return textOptions.default;
    }
  })();

  return (
    <FinanceCard
      heading="Co-signer"
      id="co-signer"
      icon={<Users color={icon.primary} size={24} />}
      text={text}
      disabled={hasError}
      showDivider={hasCosigner}
      Switch={<CardToggle />}
    >
      {hasCosigner ? (
        <CoSignerContent>
          <Avatar>
            <Initials>{coSignerInitials}</Initials>
          </Avatar>

          <CoSignerDetails>
            <Name>{`${coSignerData?.firstName} ${coSignerData?.lastName}`}</Name>
            <LinkButton
              data-analytics-id="Financing Co-Signer Edit Link"
              disabled={isPending || !isHydrated}
              size="sm"
              onClick={handleEditCoSigner}
              data-testid="edit-co-signer"
            >
              Edit
            </LinkButton>
          </CoSignerDetails>
        </CoSignerContent>
      ) : (
        !hasError && (
          <HydrationGatedButton
            className="self-start"
            data-analytics-id="Financing Co-Signer Add Button"
            size="sm"
            variant="secondary"
            onClick={handleAddCosigner}
            data-testid="add-co-signer"
          >
            Add a Co-signer
          </HydrationGatedButton>
        )
      )}
    </FinanceCard>
  );
}
